import { Head, Link, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"
import { track } from "@/lib/analytics"
import PublicLayout from "@/components/public/layout"
import { VideoPhone } from "@/components/public/video-phone"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function RequestDemo({ meta }: { meta: SeoMeta }) {
  const form = useForm({
    lead_type: "demo" as const,
    name: "",
    email: "",
    phone: "",
    agency_name: "",
    active_listings: "",
    message: "",
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    track("demo_request_submitted")
    form.post("/demo")
  }

  const inputClasses =
    "w-full rounded-md border border-stone bg-white px-4 py-3 text-sm text-charcoal placeholder:text-warm-grey/60 transition-colors focus:border-red/40 focus:outline-none focus:ring-2 focus:ring-red/10"
  const errorClasses = "mt-1 text-xs text-red"
  const labelClasses = "block text-sm font-medium text-charcoal"

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />
      </Head>

      <section className="px-6 py-12 md:py-16">
        <div className="mx-auto max-w-5xl">
          <div className="grid items-start gap-10 lg:grid-cols-[1fr,minmax(0,420px)] lg:gap-14">

            {/* Left — form (shown first on mobile via order) */}
            <div className="order-2 lg:order-1">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                Request a demo
              </p>
              <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-charcoal md:text-4xl">
                See QuiteRight in action<span className="text-red">.</span>
              </h1>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-slate">
                Book a 20-minute walkthrough. We&apos;ll use your own listings
                to show you how it works. No sales pitch, no commitment.
              </p>

              {/* Inline benefits */}
              <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
                {["20 min, no commitment", "Your properties, live", "Ask us anything"].map((b) => (
                  <span key={b} className="flex items-center gap-1.5 text-xs text-charcoal">
                    <svg viewBox="0 0 20 20" fill="currentColor" className="size-3.5 text-red">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                    </svg>
                    {b}
                  </span>
                ))}
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="mt-8">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="demo-name" className={labelClasses}>
                      Full name <span className="text-red">*</span>
                    </label>
                    <input
                      id="demo-name"
                      type="text"
                      required
                      value={form.data.name}
                      onChange={(e) => form.setData("name", e.target.value)}
                      placeholder="Jane Smith"
                      className={`mt-1 ${inputClasses}`}
                    />
                    {form.errors.name && <p className={errorClasses}>{form.errors.name}</p>}
                  </div>

                  <div>
                    <label htmlFor="demo-email" className={labelClasses}>
                      Work email <span className="text-red">*</span>
                    </label>
                    <input
                      id="demo-email"
                      type="email"
                      required
                      value={form.data.email}
                      onChange={(e) => form.setData("email", e.target.value)}
                      placeholder="jane@agency.co.uk"
                      className={`mt-1 ${inputClasses}`}
                    />
                    {form.errors.email && <p className={errorClasses}>{form.errors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="demo-phone" className={labelClasses}>
                      Phone number
                    </label>
                    <input
                      id="demo-phone"
                      type="tel"
                      value={form.data.phone}
                      onChange={(e) => form.setData("phone", e.target.value)}
                      placeholder="07700 900 000"
                      className={`mt-1 ${inputClasses}`}
                    />
                  </div>

                  <div>
                    <label htmlFor="demo-agency" className={labelClasses}>
                      Agency name <span className="text-red">*</span>
                    </label>
                    <input
                      id="demo-agency"
                      type="text"
                      required
                      value={form.data.agency_name}
                      onChange={(e) => form.setData("agency_name", e.target.value)}
                      placeholder="Clifton Property Group"
                      className={`mt-1 ${inputClasses}`}
                    />
                    {form.errors.agency_name && <p className={errorClasses}>{form.errors.agency_name}</p>}
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="demo-listings" className={labelClasses}>
                    Active listings
                  </label>
                  <select
                    id="demo-listings"
                    value={form.data.active_listings}
                    onChange={(e) => form.setData("active_listings", e.target.value)}
                    className={`mt-1 ${inputClasses} appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%236B6B6B%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.22%208.22a.75.75%200%200%201%201.06%200L10%2011.94l3.72-3.72a.75.75%200%201%201%201.06%201.06l-4.25%204.25a.75.75%200%200%201-1.06%200L5.22%209.28a.75.75%200%200%201%200-1.06Z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] bg-[length:20px] bg-[right_12px_center] bg-no-repeat pr-10`}
                  >
                    <option value="">Select…</option>
                    <option value="1-10">1–10</option>
                    <option value="11-25">11–25</option>
                    <option value="26-50">26–50</option>
                    <option value="51-100">51–100</option>
                    <option value="100+">100+</option>
                  </select>
                </div>

                <div className="mt-4">
                  <label htmlFor="demo-message" className={labelClasses}>
                    Anything you&apos;d like us to cover?{" "}
                    <span className="font-normal text-warm-grey">(optional)</span>
                  </label>
                  <textarea
                    id="demo-message"
                    rows={2}
                    value={form.data.message}
                    onChange={(e) => form.setData("message", e.target.value)}
                    placeholder="E.g. 'Show us social publishing' or paste a listing URL."
                    className={`mt-1 resize-none ${inputClasses}`}
                  />
                </div>

                <button
                  type="submit"
                  disabled={form.processing}
                  className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-red px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-deep-red disabled:opacity-60"
                >
                  {form.processing ? "Sending…" : "Request a demo"}
                </button>

                <p className="mt-3 text-center text-xs text-warm-grey">
                  We&apos;ll respond within one working day.{" "}
                  <Link href="/privacy" className="text-red hover:text-deep-red">
                    Privacy Policy
                  </Link>
                </p>
              </form>

              {/* Social proof */}
              <div className="mt-8 rounded-xl border border-stone bg-cream/40 px-5 py-4">
                <p className="text-sm italic leading-relaxed text-slate">
                  &ldquo;We booked a demo expecting a generic slide deck. Instead they
                  created a video from one of our live listings in front of us. We signed
                  up the same day.&rdquo;
                </p>
                <p className="mt-2 text-xs font-medium text-charcoal">
                  Independent agency, Bristol
                </p>
              </div>
            </div>

            {/* Right — video preview (hidden on mobile, visual anchor on desktop) */}
            <div className="order-1 flex justify-center lg:sticky lg:top-24 lg:order-2 lg:justify-end">
              <div className="hidden lg:block">
                <VideoPhone />
                <p className="mt-3 text-center text-xs text-warm-grey">
                  Made from listing photos. No filming required.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
